<template>
  <div class="requisite-card">
    <div class="head">
      {{rel.code}}
      <span v-if="rel.credits">{{rel.credits}} credit points</span>
    </div>
    <div class="title">
      {{rel.title}}
    </div>

    <template v-if="someUnitsSelected">

      <!-- Requisite met / added -->
      <div v-if="unitCodeFound && displayRequirements" class="requisite__alert-bottom success">
        <span class="requisite__alert-bottom-content">
          Pre-requisite met.
        </span>
        <transition name="pop">
          <span class="ml-2" v-if="displaySuccessSnack">
            Added to {{ displaySuccessSnackContainer }}
          </span>
        </transition>
      </div>

      <!-- Unable to find unit (no add button) -->
      <div class="requisite__alert-bottom warning" v-if="!canAdd && displayRequirements && !unitCodeFound && !loading && !error"
        :id="`tooltip-req-unavailable-${rel.code}`">
        <span class="requisite__alert-bottom-content">
          <i class="far fa-exclamation-circle mr-1"></i> Pre-requisite not available <span v-if="!available">in your location</span>
        </span>
      </div>

      <!-- Add button -->
      <div class="requisite__select-bottom" v-if="canAdd && displayRequirements && !unitCodeFound && !error">
        <button type="button" class="btn-link-bold" v-on:click="addUnit()">
          <i class="fal fa-plus mr-1"></i> Add pre-requisite
        </button>
      </div>

      <!-- Exclusion found! -->
      <div v-if="unitCodeFound && displayExclusions" class="requisite__alert-bottom warning" :id="`tooltip-req-exclusion-${rel.code}`">
        <span class="requisite__alert-bottom-content">
          {{ __("label:exclusion conflict") }} <i class="far fa-exclamation-circle mr-1"></i>
        </span>
      </div>

      <!-- Loading state -->
      <transition name="fade">
        <div class="overlay-state overlay-state-loading" v-if="loading">
          <div class="overlay-state__body">
            <i class="overlay-state__icon far fa-spinner-third fa-spin"></i>
            <div class="overlay-state__header">{{ loadingMessage }}</div>
          </div>
        </div>
        <div class="overlay-state overlay-state-loading" v-else-if="error && !loading">
          <div class="overlay-state__body">
            <div class="overlay-state__header">Sorry, details cannot be loaded, please check your connection.</div>
            <button type="button" class="btn-link-bold mt-1" v-on:click="loadRelationshipUnit()">
              Try again
            </button>
          </div>
        </div>
      </transition>

      <b-tooltip :target="`tooltip-req-unavailable-${rel.code}`" triggers="hover" placement="top">
        This pre-requisite cannot be added as it is either unavailable or cannot be located in general electives. Please consult the handbook or contact your
        academic chair for more information.
      </b-tooltip>

      <b-tooltip :target="`tooltip-req-exclusion-${rel.code}`" triggers="hover" placement="top">
        {{ __("info:exclusion conflict") }}
      </b-tooltip>

    </template>
  </div>

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Curriculum from 'client/js/models/curriculum'

export default {
  props: {
    rel: {
      type: Object,
      required: true
    },
    course: {
      type: Object,
      required: false
    },
    unitCodeFound: {
      type: Boolean,
      required: true
    },
    displayRequirements: {
      type: Boolean,
      required: true
    },
    displayExclusions: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      loadingMessage: '',
      displaySuccessSnack: false,
      displaySuccessSnackContainer: null,
      canAdd: false,
      available: true
    }
  },
  methods: {
    ...mapActions({
      attachStructure: 'courses/attachStructure',
      addRelationships: 'courses/addRelationships',

      addItem: 'units/addCurriculumItem'
    }),

    getGeneralElectiveStructure() {
      if (this.course) {
        const generalElectiveStructure = this.course.structure[0].containers[this.course.structure[0].containers.length - 1].container.filter(
          (x) => x.verticalGrouping == 'General Elective'
        )
        return generalElectiveStructure.length > 0 ? generalElectiveStructure[0] : null
      }
    },

    async getRelationshipUnit() {
      const generalElectives = this.getGeneralElectiveStructure()

      let returnVal = null
      if (generalElectives?.conditionSet?.length) {
        // const query = generalElectives.conditionSet[0].query
        // const searchQuery = `condition-set-url=${query}&title-or-code-search-term=${this.rel.code}`
        // const ConditionSetResponse = await Curriculum.searchConditionSet(searchQuery)

        // const ResponseItems = ConditionSetResponse?.items?.filter((c) => c.code == this.rel.code)
        const conditionSetId = generalElectives.cl_id
        const SearchResponse = await Curriculum.searchCachedConditionSet(conditionSetId, this.rel.code, this.includeDraftVersions)
        const ResponseItems = SearchResponse?.items?.filter((c) => c.code == this.rel.code)
        if (ResponseItems.length > 0) {
          const ResponseRelationshipItem = ResponseItems[0]

          //add unit 'cos we actually haven't got it yet
          try {
            ResponseRelationshipItem.unit = await Curriculum.getUnit(ResponseRelationshipItem.code, ResponseRelationshipItem.version)
            returnVal = ResponseRelationshipItem
          } catch (err) {
            this.error = err
          }

          if (this.selectedPreferences?.studentType === 'Transnational' && ResponseRelationshipItem.type == 'Unit' && ResponseRelationshipItem.unit.offerings) {
            // Check place of residence vs offerings for Transnationals
            const placeOfResidence = this.selectedPreferences.placeOfResidence
            console.log(`Looking for ${placeOfResidence} in:`, ResponseRelationshipItem.unit.offerings.map(x => x.location.id))
            this.available = ResponseRelationshipItem.unit.offerings.some((x) => x.location.id === placeOfResidence)
          }
        }
      }
      this.loading = false
      return returnVal
    },

    async addUnit() {
      this.loadingMessage = 'Applying selection'
      this.loading = true
      try {
        const containerFirst = this.course.structure[0].containers[this.course.structure[0].containers.length - 1]
        const generalElectives = this.getGeneralElectiveStructure()
console.log('generalElectives', generalElectives)
        if (generalElectives?.conditionSet?.length) {

          // const query = generalElectives.conditionSet[0].query
          // const searchQuery = `condition-set-url=${query}&title-or-code-search-term=${this.rel.code}`
          // const ConditionSetResponse = await Curriculum.searchConditionSet(searchQuery)

          // const ResponseItems = ConditionSetResponse?.items?.filter((c) => c.code == this.rel.code)

          // Alternative search using cached condition set
          const conditionSetId = generalElectives.cl_id
          const SearchResponse = await Curriculum.searchCachedConditionSet(conditionSetId, this.rel.code, this.includeDraftVersions)
          const ResponseItems = SearchResponse?.items?.filter((c) => c.code == this.rel.code)

          if (ResponseItems.length > 0) {
            const ResponseRelationshipItem = ResponseItems[0]

            //add unit 'cos we actually haven't got it yet
            ResponseRelationshipItem.unit = await Curriculum.getUnit(ResponseRelationshipItem.code, ResponseRelationshipItem.version)
            //  This assumes only two levels
            const electiveLocation =
              'root' + '>' + containerFirst.title + '-' + containerFirst.order + '>' + generalElectives.title + '-' + generalElectives.order
            const newLocation = electiveLocation + '>' + ResponseRelationshipItem.code

            // Add items to Journey's curriculum items
            this.addItem({
              firstLevelTitle: containerFirst.title, // this.firstLevelTitle,
              relationship: ResponseRelationshipItem,
              location: newLocation,
              verticalGrouping: generalElectives.verticalGrouping // this.verticalGrouping
            })

            // Add new item to the journeys course to the relationship structure
            this.addRelationships({
              location: electiveLocation,
              relationships: [ResponseRelationshipItem]
            })
            this.showSuccessSnack(generalElectives.title)
          }
        }
      } catch (err) {
        this.error = err
      }
      this.loading = false
    },
    showSuccessSnack(container) {
      setTimeout(() => ((this.displaySuccessSnack = true), (this.displaySuccessSnackContainer = container)), 250)
      setTimeout(() => (this.displaySuccessSnack = false), 4000)
    },
    async loadRelationshipUnit() {
      if (this.displayRequirements == true || this.displayExclusions == true) {
        this.loadingMessage = 'Loading...'
        this.loading = true
        this.error = null
        try {
          const relationshipUnit = await this.getRelationshipUnit()
          this.canAdd = relationshipUnit !== null && (relationshipUnit?.unit !== undefined || relationshipUnit?.unit !== null) && this.available
        } catch (err) {
          this.error = err
        }
        this.loading = false
      }
    }
  },
  computed: {
    ...mapGetters({
      selectedUnitCodes: 'units/getSelectedUnitCodes',
      selectedPreferences: 'preferences/getSelectedStudyPreferences',
      includeDraftVersions: 'courses/includeDraftVersions',
    }),
    someUnitsSelected() {
      return this.selectedUnitCodes.length > 0
    }
  },
  mounted: async function () {
    await this.loadRelationshipUnit()
  }
}
</script>
